/**
 * @file Drives every authored character body's `AnimationMixer`.
 *
 * {@link ../rendering/CharacterModels.js} stashes `{ mixer, actions, current }`
 * on `Renderable.object.userData.animation` for any entity built from a real
 * (non-procedural) body — this system is the only thing that reads it. A body
 * without that data (the procedural capsule fallback, every enemy today) is
 * skipped outright, so this costs nothing until more archetypes get real art.
 */

import { System } from '../ecs/System.js';
import { Transform, Velocity, Renderable, Weapon } from '../components/index.js';
import { RUN_SPEED_THRESHOLD, ANIMATION_FADE_SECONDS, poseAiming } from '../rendering/CharacterModels.js';

export class CharacterAnimationSystem extends System {
  // After RenderSystem: a body attached to the scene this frame already has
  // its mixer's clip actions bound to the live (cloned) skeleton.
  static priority = 101;

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    for (const entity of this.world.query(Transform, Renderable, Velocity)) {
      const object = entity.get(Renderable).object;
      const anim = object.userData.animation;
      if (!anim) continue;

      anim.mixer.update(dt);

      const speed = entity.get(Velocity).linear.length();
      const next = speed > RUN_SPEED_THRESHOLD ? anim.actions.run : anim.actions.idle;
      if (next && next !== anim.current) {
        next.reset().fadeIn(ANIMATION_FADE_SECONDS).play();
        anim.current?.fadeOut(ANIMATION_FADE_SECONDS);
        anim.current = next;
      }

      // No aim/fire clip in the source pack — the right arm extends toward a
      // procedural pose instead (see CharacterModels.js) while the trigger is
      // held, and eases back once it's released.
      const firing = entity.get(Weapon)?.triggerHeld ?? false;
      poseAiming(object, firing ? 1 : 0, dt);
    }
  }
}
